import React, { useState, Component } from 'react';
import { Modal } from '../ui/Modal';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { useOrderDetail } from '../../lib/order-detail-hooks';
import { MapPin, Phone, Clock, Map, Navigation, Receipt, CreditCard, UtensilsCrossed, AlertTriangle } from 'lucide-react';
import { formatCurrency } from '../../lib/utils';
import { supabase } from '../../lib/supabase';
import { VoidConfirmModal } from './VoidConfirmModal';
import { logVoidAudit, updateVoidedColumns } from '../../lib/void-audit';
interface OrderDetailDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  orderId: string | null;
  onStatusChange?: () => void;
}
const formatTimeAgo = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / 60000);
  if (diffInMinutes < 1) return 'Hace un momento';
  if (diffInMinutes < 60) return `Hace ${diffInMinutes} min`;
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `Hace ${diffInHours} h`;
  return date.toLocaleDateString();
};
export function OrderDetailDrawer({
  isOpen,
  onClose,
  orderId,
  onStatusChange
}: OrderDetailDrawerProps) {
  const {
    order,
    loading,
    error
  } = useOrderDetail(orderId);
  const [voidModalOpen, setVoidModalOpen] = useState(false);
  if (!isOpen) return null;
  const handleStatusChange = async (newStatus: string) => {
    if (!order) return;
    try {
      const {
        error
      } = await supabase.from('orders').update({
        status: newStatus
      }).eq('id', order.id);
      if (error) throw error;
      if (onStatusChange) onStatusChange();
      onClose();
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Error al actualizar el estado');
    }
  };
  const handleVoidConfirm = async (authorizedBy: string, reason: string) => {
    if (!order) return;
    try {
      await updateVoidedColumns('orders', Number(order.id), authorizedBy, reason);
      await logVoidAudit({
        restaurant_id: order.restaurant_id,
        order_id: Number(order.id),
        action_type: 'cancel_order',
        authorized_by: authorizedBy,
        reason,
        original_amount: order.total || 0
      });
      await handleStatusChange('cancelled');
    } catch (err) {
      console.error('Error cancelling order:', err);
      alert('Error al anular el pedido');
    } finally {
      setVoidModalOpen(false);
    }
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'accepted':
        return 'info';
      case 'preparing':
        return 'info';
      case 'ready':
        return 'success';
      case 'delivered':
        return 'default';
      case 'cancelled':
        return 'danger';
      default:
        return 'default';
    }
  };
  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Pendiente';
      case 'accepted':
        return 'Aceptado';
      case 'preparing':
        return 'Preparando';
      case 'ready':
        return 'Listo';
      case 'delivered':
        return 'Entregado';
      case 'cancelled':
        return 'Cancelado';
      default:
        return status;
    }
  };
  const renderOptions = (options: any[]) => {
    if (!options || options.length === 0) return null;
    const grouped = options.reduce((acc, opt) => {
      const group = opt.option_group || 'Otros';
      if (!acc[group]) acc[group] = [];
      acc[group].push(opt);
      return acc;
    }, {} as Record<string, any[]>);
    const groupOrder = ['Base', 'Proteína', 'Toppings', 'Premium', 'Aderezos', 'Semillas'];
    const sortedGroups = Object.keys(grouped).sort((a, b) => {
      const indexA = groupOrder.indexOf(a);
      const indexB = groupOrder.indexOf(b);
      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      return a.localeCompare(b);
    });
    return <div className="mt-2 text-sm text-gray-600 dark:text-gray-400 pl-4 border-l-2 border-gray-200 dark:border-gray-700" data-id="element-1299" data-mp="3fhijg5o524ce">
        {sortedGroups.map(group => <div key={group} className="mb-1" data-id="element-1300" data-mp="3bgqazen3c3h6">
            <span className="font-semibold text-gray-700 dark:text-gray-300" data-id="element-1301" data-mp="2nq2s5ybmztfu">
              {group}:{' '}
            </span>
            {grouped[group].map((opt, idx) => <span key={opt.id} data-id="element-1302" data-mp="2itm391wm5b1p" data-mpi={idx}>
                {opt.option_name_snapshot}
                {opt.option_price_snapshot > 0 && ` (+${formatCurrency(opt.option_price_snapshot)})`}
                {idx < grouped[group].length - 1 ? ', ' : ''}
              </span>)}
          </div>)}
      </div>;
  };
  return <Modal isOpen={isOpen} onClose={onClose} title={order ? `Pedido #${order.order_number}` : 'Detalle del Pedido'} data-id="element-1303" data-mp="70ovmrftpvvg">
      {loading ? <div className="p-8 text-center text-gray-500 dark:text-gray-400" data-id="element-1304" data-mp="jkce861o3ak4">
          Cargando detalles...
        </div> : error ? <div className="p-8 text-center text-red-500" data-id="element-1305" data-mp="5lkzqiiw7mvg">
          Error al cargar el pedido
        </div> : order ? <div className="space-y-6" data-id="element-1306" data-mp="ajwb4xou65fs">
          {/* 1. HEADER */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg" data-id="element-1307" data-mp="ya9v00x9oxza">
            <div data-id="element-1308" data-mp="13aedfwhqk2s5">
              <div className="flex items-center gap-3 mb-2" data-id="element-1309" data-mp="ojf7yuc75ucs">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-1310" data-mp="oiw0y8o34qdj">
                  #{order.order_number}
                </h2>
                <Badge variant={getStatusColor(order.status) as any} data-id="element-1311" data-mp="1h82zv8rgbhzb">
                  {getStatusText(order.status)}
                </Badge>
                <Badge variant={order.delivery_type === 'delivery' ? 'info' : 'default'} data-id="element-1312" data-mp="1lzl01ulnhej8">
                  {order.delivery_type === 'delivery' ? 'Delivery' : order.delivery_type === 'pickup' ? 'Para Llevar' : 'En Mesa'}
                </Badge>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400" data-id="element-1313" data-mp="1huqxbrtpchon">
                <Clock className="w-4 h-4" data-id="element-1314" data-mp="1cx8jpo4qpeab" />
                <span data-id="element-1315" data-mp="185h05xk048to">{formatTimeAgo(order.created_at)}</span>
              </div>
            </div>
            <div className="text-left md:text-right" data-id="element-1316" data-mp="135unbp834ule">
              <div className="font-medium text-gray-900 dark:text-white" data-id="element-1317" data-mp="136dvie7xo4m8">
                {order.customer_name || 'Cliente sin nombre'}
              </div>
              {order.customer_phone && <a href={`tel:${order.customer_phone}`} className="flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:underline mt-1 justify-start md:justify-end" data-id="element-1318" data-mp="u4mqzcp4q3ty">
                  <Phone className="w-4 h-4" data-id="element-1319" data-mp="35srl2u2irn40" />
                  {order.customer_phone}
                </a>}
            </div>
          </div>

          {/* 2. DELIVERY SECTION */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4" data-id="element-1320" data-mp="39sjvlcbh43c0">
            <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2" data-id="element-1321" data-mp="3erdb1aabm4et">
              <MapPin className="w-4 h-4" data-id="element-1322" data-mp="2m4af8px147s9" />
              Detalles de Entrega
            </h3>
            {order.delivery_type === 'delivery' && <div className="space-y-3" data-id="element-1323" data-mp="2r2kb72qkvcex">
                <p className="text-gray-900 dark:text-white" data-id="element-1324" data-mp="2vv5667l7zvk1">
                  {order.delivery_address || 'Sin dirección especificada'}
                </p>
                {order.maps_url && <div className="flex gap-3" data-id="element-1325" data-mp="23e13aobfx1nd">
                    <a href={order.maps_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:underline" data-id="element-1326" data-mp="23cpziohzvtw9">
                      <Map className="w-4 h-4" data-id="element-1327" data-mp="15t82556x2biv" /> Google Maps
                    </a>
                    <a href={`https://waze.com/ul?q=${encodeURIComponent(order.delivery_address || '')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:underline" data-id="element-1328" data-mp="11p09iwxc8bfd">
                      <Navigation className="w-4 h-4" data-id="element-1329" data-mp="1phgw9ks0srl0" /> Waze
                    </a>
                  </div>}
              </div>}
            {order.delivery_type === 'dine_in' && <p className="text-gray-900 dark:text-white font-medium" data-id="element-1330" data-mp="1th92hqtxyln7">
                Mesa: {order.table_number || 'No especificada'}
              </p>}
            {order.delivery_type === 'pickup' && <p className="text-gray-900 dark:text-white font-medium" data-id="element-1331" data-mp="2i2dv216s6i5q">
                Pasará a recoger
              </p>}
          </div>

          {/* 3. PAYMENT SECTION */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4" data-id="element-1332" data-mp="283ew5abti0vx">
            <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2" data-id="element-1333" data-mp="1ucjgnf6tcljd">
              <CreditCard className="w-4 h-4" data-id="element-1334" data-mp="1z4b8ru5mazbm" />
              Pago
            </h3>
            <div className="flex flex-col gap-1" data-id="element-1335" data-mp="1kf53bog6scva">
              <p className="text-gray-900 dark:text-white capitalize" data-id="element-1336" data-mp="1kcakg1ctn03z">
                Método:{' '}
                <span className="font-medium" data-id="element-1337" data-mp="1pbdr1q8g9ee2">
                  {order.payment_method === 'cash' ? 'Efectivo' : order.payment_method === 'card' ? 'Tarjeta' : order.payment_method === 'transfer' ? 'Transferencia' : order.payment_method}
                </span>
              </p>
              {order.payment_method === 'cash' && order.cash_amount && <>
                  <p className="text-gray-600 dark:text-gray-400" data-id="element-1338" data-mp="2yrjhh9cr4jk8">
                    Paga con: {formatCurrency(order.cash_amount)}
                  </p>
                  <p className="text-gray-600 dark:text-gray-400" data-id="element-1339" data-mp="3rexawxh8qwzl">
                    Cambio:{' '}
                    {formatCurrency(Math.max(0, order.cash_amount - order.total))}
                  </p>
                </>}
            </div>
          </div>

          {/* 4. ORDER ITEMS SECTION */}
          <div data-id="element-1340" data-mp="e7cb8t632i2f">
            <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2" data-id="element-1341" data-mp="e6spt2h85bjn">
              <UtensilsCrossed className="w-4 h-4" data-id="element-1342" data-mp="izltro4vcfuo" />
              Artículos del Pedido
            </h3>
            {(!order.items || order.items.length === 0) && <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-sm" data-id="element-1343" data-mp="oc2gp90p44be">
                <p className="font-semibold text-red-800 dark:text-red-300 mb-1" data-id="element-1344" data-mp="1225vxg15qxrc">
                  ⚠️ No se pudieron cargar los artículos de este pedido
                </p>
                <p className="text-red-700 dark:text-red-400" data-id="element-1345" data-mp="9d8gdkobzf2a">
                  Order ID: <code data-id="element-1346" data-mp="5cohabk761di">{order.id}</code> — total ₡{order.total}. Esto
                  suele ser un problema de permisos (RLS) o que los artículos no
                  se guardaron al crear la orden. Revisa la consola del
                  navegador y la tabla <code data-id="element-1347" data-mp="ecbmz9jyltcd">order_items</code> en Supabase.
                </p>
              </div>}
            <div className="space-y-3" data-id="element-1348" data-mp="1uprxkiqtrsbk">
              {order.items.map((item: any) => <div key={item.id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4" data-id="element-1349" data-mp="1prgq2eo05twi">
                  <div className="flex justify-between items-start mb-2" data-id="element-1350" data-mp="21ti10x2xld26">
                    <div data-id="element-1351" data-mp="26tw0vrudpgp4">
                      <div className="flex items-center gap-2" data-id="element-1352" data-mp="26unw31k2dcev">
                        <span className="font-bold text-gray-900 dark:text-white" data-id="element-1353" data-mp="2bsh0pq5pxyd1">
                          {item.quantity}x
                        </span>
                        <span className="font-semibold text-gray-900 dark:text-white" data-id="element-1354" data-mp="1e6s0k068dqjo">
                          {item.item_name_snapshot}
                        </span>
                      </div>
                      {item.station_code && <Badge variant="default" className="mt-1 text-xs" data-id="element-1355" data-mp="1stl8ulhc1w2d">
                          {item.station_code}
                        </Badge>}
                    </div>
                    <span className="font-medium text-gray-900 dark:text-white" data-id="element-1356" data-mp="10daeo8q2o1qh">
                      {formatCurrency(item.line_total)}
                    </span>
                  </div>

                  {renderOptions(item.options)}

                  {item.notes && <div className="mt-3 text-sm text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 p-2 rounded flex items-start gap-2" data-id="element-1357" data-mp="nt3skifbs17b">
                      <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" data-id="element-1358" data-mp="dvz246eg5jkp" />
                      <p data-id="element-1359" data-mp="9vozn3pv5bal">Nota: {item.notes}</p>
                    </div>}
                </div>)}
            </div>
          </div>

          {/* 5. GENERAL NOTES SECTION */}
          {(order.notes || order.allergies) && <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/30 rounded-lg p-4" data-id="element-1360" data-mp="39g41yx4enl">
              <h3 className="text-sm font-semibold text-amber-800 dark:text-amber-500 uppercase tracking-wider mb-2" data-id="element-1361" data-mp="3mcc2i7hzntif">
                Notas Generales y Alergias
              </h3>
              {order.allergies && <p className="text-amber-900 dark:text-amber-400 font-medium mb-1" data-id="element-1362" data-mp="avyw4expj6xl">
                  Alergias: {order.allergies}
                </p>}
              {order.notes && <p className="text-amber-800 dark:text-amber-300" data-id="element-1363" data-mp="24q920h3reef">
                  {order.notes}
                </p>}
            </div>}

          {/* 6. TOTALS SECTION */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-2" data-id="element-1364" data-mp="3oavjmjrkwyp9">
            <div className="flex justify-between text-gray-600 dark:text-gray-400" data-id="element-1365" data-mp="3tarbjkn04zog">
              <span data-id="element-1366" data-mp="3fagmmn595v5x">Subtotal</span>
              <span data-id="element-1367" data-mp="3fbi9dovms63h">{formatCurrency(order.subtotal)}</span>
            </div>
            {order.delivery_fee > 0 && <div className="flex justify-between text-gray-600 dark:text-gray-400" data-id="element-1368" data-mp="3jck8m5pjsi5g">
                <span data-id="element-1369" data-mp="1dkew0rucwdf8">Costo de envío</span>
                <span data-id="element-1370" data-mp="1dh97petjxfkk">{formatCurrency(order.delivery_fee)}</span>
              </div>}
            <div className="flex justify-between text-xl font-bold text-gray-900 dark:text-white pt-2 border-t border-gray-200 dark:border-gray-700" data-id="element-1371" data-mp="1hicn607ot9l9">
              <span data-id="element-1372" data-mp="2y25uivyth0z7">Total</span>
              <span data-id="element-1373" data-mp="2y2z382hc60jz">{formatCurrency(order.total)}</span>
            </div>
          </div>

          {/* 7. LINKS SECTION */}
          <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-200 dark:border-gray-700" data-id="element-1374" data-mp="371sy9of0qivf">
            {order.tracking_token && <Button variant="secondary" size="sm" onClick={() => window.open(`/tracking/${order.tracking_token}`, '_blank')} data-id="element-1375" data-mp="3lpnuhes8j5d9">
                Ver Tracking
              </Button>}
            {order.ticket_image_url && <Button variant="secondary" size="sm" onClick={() => window.open(order.ticket_image_url, '_blank')} data-id="element-1376" data-mp="3pq7owalfbuvp">
                <Receipt className="w-4 h-4 mr-2" data-id="element-1377" data-mp="1vfu9tx5iofx5" />
                Ver Ticket
              </Button>}
          </div>

          {/* ACTIONS */}
          <div className="flex flex-wrap gap-2 pt-6 mt-6 border-t border-gray-200 dark:border-gray-700" data-id="element-1378" data-mp="49zkawmzg3ru">
            {order.status === 'pending' && <Button variant="primary" onClick={() => handleStatusChange('accepted')} data-id="element-1379" data-mp="980yp49grewz">
                Aceptar Pedido
              </Button>}
            {(order.status === 'pending' || order.status === 'accepted') && <Button variant="secondary" onClick={() => handleStatusChange('preparing')} data-id="element-1380" data-mp="n7sjpoyedvkh">
                Marcar Preparando
              </Button>}
            {order.status === 'preparing' && <Button variant="primary" onClick={() => handleStatusChange('ready')} data-id="element-1381" data-mp="110ry1nmg1jqg">
                Marcar Listo
              </Button>}
            {order.status === 'ready' && <Button variant="primary" onClick={() => handleStatusChange('delivered')} data-id="element-1382" data-mp="2qif2hwj5w8sm">
                Marcar Entregado
              </Button>}
            {order.status !== 'cancelled' && order.status !== 'delivered' && <Button variant="danger" onClick={() => setVoidModalOpen(true)} data-id="element-1383" data-mp="2ciw969v0f20d">
                Cancelar
              </Button>}
          </div>
        </div> : null}
      <VoidConfirmModal isOpen={voidModalOpen} onClose={() => setVoidModalOpen(false)} onConfirm={handleVoidConfirm} title="Anular Pedido" description="Por favor, indica quién autoriza y el motivo de la anulación del pedido. Esta acción quedará registrada en el reporte de caja." data-id="element-1384" data-mp="35607jvzlki36" />
    </Modal>;
}